// @ vendors
const { Promise } = require('es6-promise');

// @ commons
const apiURLBuilder = require('core/apiURLBuilder');
const { APIPost } = require('utilities/APIRequestHelper');
const { flow, filter, get, map } = require('lodash');
const { formatText } = require('core/i18n').i18n;

// @ utilities
const { recordProcess, PROC_END, PROC_FAIL } = require('utilities/tagging');

// @ track processes
const { PROC_CHANGE_WORK_DATA } = require('constants/mortgageRequestProcesses');

// @ constants
const actionTypes = require('constants/actionTypes');
const { NOM, VL } = require('constants/mortgageRequestDocumentsTypes');
const { FUNCIONARIO_MUFACE } = require('constants/mortgageRequestHolderData').EMPLOYMENT_SITUATION;

// @ actions
const {
    requestDataWork,
    requestDataProprietorInProgress,
    requestDataProprietorSuccess,
    requestDataProprietorFailure,
} = require('actions/mortgageRequest/mortgageRequestDataProprietor');
const { requestState } = require('actions/mortgageRequest/mortgageRequestState');
const { getHoldersPersonalAndEconomicData } = require('actions/mortgageRequest/mortgageRequestViewProcess');
const { validateHolderStep, getNextStepValid } = require('actions/mortgageRequest/mortgageHoldersDataValidation');

const requestHolderWorkInProgress = () => ({
    type: actionTypes.MORTGAGE_REQUEST_FETCH_HOLDER_WORK_REQUEST
});

const requestHolderWorkSuccess = data => ({
    type: actionTypes.MORTGAGE_REQUEST_FETCH_HOLDER_WORK_SUCCESS,
    data
});

const requestHolderWorkFailure = error => ({
    type: actionTypes.MORTGAGE_REQUEST_FETCH_HOLDER_WORK_FAILURE,
    data: { error }
});

const uploadHolderWorkInProgress = () => ({
    type: actionTypes.MORTGAGE_REQUEST_UPLOAD_HOLDER_WORK_REQUEST
});

const uploadHolderWorkSuccess = data => ({
    type: actionTypes.MORTGAGE_REQUEST_UPLOAD_HOLDER_WORK_SUCCESS,
    data
});

const uploadHolderWorkFailure = error => ({
    type: actionTypes.MORTGAGE_REQUEST_UPLOAD_HOLDER_WORK_FAILURE,
    data: { error }
});

const uploadWorkFileInProgress = () => ({
    type: actionTypes.MORTGAGE_REQUEST_UPLOAD_HOLDER_WORK_FILE_REQUEST
});

const uploadWorkFileSuccess = () => ({
    type: actionTypes.MORTGAGE_REQUEST_UPLOAD_HOLDER_WORK_FILE_SUCCESS
});

const uploadWorkFileFailure = error => ({
    type: actionTypes.MORTGAGE_REQUEST_UPLOAD_HOLDER_WORK_FILE_FAILURE,
    data: { error }
});

const requestWorkFlag = flag => ({
    type: actionTypes.MORTGAGE_REQUEST_WORK_FLAG,
    flag
});

const resetState = () => ({
    type: actionTypes.MORTGAGE_REQUEST_HOLDER_WORK_RESET
});

const resetWorkDataState = () => ({
    type: actionTypes.MORTGAGE_REQUEST_UPLOAD_HOLDER_WORK_RESET
});

const cleanState = () => dispatch => dispatch(resetWorkDataState());

const reset = () => dispatch => dispatch(resetState());

const requestHolderWork = (tipoDocumentoIdentidad, numeroDocumento) => (dispatch, getState) => {
    const currentState = getState().mortgageRequestWorkData;
    const isAlreadyFetching = currentState.get('isFetching');

    if (!isAlreadyFetching) {
        const requestData = {
            query: {
                tipoDocumentoIdentidad,
                numeroDocumento
            }
        };
        dispatch(requestHolderWorkInProgress());
        return APIPost(dispatch, apiURLBuilder.getURL('mortgageRequesWorkDataRequest'), requestData)
            .then(response => {
                dispatch(requestHolderWorkSuccess(response));
                getHoldersPersonalAndEconomicData(dispatch, getState, true);
            })
            .catch(response => {
                if (!!response.error) {
                    dispatch(requestHolderWorkFailure(response.error));
                } else {
                    throw response;
                }
            });
    } else {
        return Promise.resolve()
    }
};

const filterHolderDocumentData = (data, holderNumber) => flow(
    data => filter(data, holder => holder.numeroTitular === holderNumber)[0],
    data => get(data, 'datosPersonales.documentoDeIdentidad')
)(data);

const uploadWorkData = (query, dispatch, holderNumber, history) => {
    dispatch(uploadHolderWorkInProgress());

    return APIPost({
        dispatch,
        endpoint: 'mortgageRequesWorkDataSubmit',
        params: { query }
    })
        .then(response => {
            recordProcess(PROC_CHANGE_WORK_DATA, PROC_END);
            dispatch(uploadHolderWorkSuccess(response));
            if (holderNumber) {
                const holderKey = holderNumber === 2 ? 'isCoHolder' : 'isHolder';
                dispatch(validateHolderStep(1, holderKey));
                const linkRedirection = dispatch(getNextStepValid(holderNumber));

                dispatch(requestState(linkRedirection, history));
            }
        })
        .catch(response => {
            if (!!response.error) {
                recordProcess(PROC_CHANGE_WORK_DATA, PROC_FAIL);
                dispatch(uploadHolderWorkFailure(response.error));
            } else {
                throw response;
            }
        });
};

const uploadWorkFile = (query, holderDocumentData, file, tipoArchivo, dispatch, getState, holderNumber, history) => {
    const idExpediente = getState().mortgageRequestState.get('idExpediente');
    const model = {
        tipoArchivo,
        idExpediente,
        tipoDocumento: holderDocumentData.tipoDocumentoIdentidad,
        numeroDocumento: holderDocumentData.identificador
    };

    const formData = new FormData();
    formData.append('files', file.file);
    formData.append(
        'model',
        new Blob([
            JSON.stringify(model)
        ],
            {
                type: 'application/json'
            })
    );

    const requestData = {
        query: formData
    };

    dispatch(uploadWorkFileInProgress());

    return APIPost({
        dispatch,
        endpoint: 'mortgageRequesWorkDataFileMultipleSubmit',
        params: requestData,
        contentType: false
    })
        .then(() => {
            dispatch(uploadWorkFileSuccess());
            uploadWorkData(query, dispatch, holderNumber, history);
        })
        .catch((response) => {
            if (!!response.error) {
                dispatch(uploadWorkFileFailure(response.error));
                uploadWorkData(query, dispatch, holderNumber, history);
            } else {
                throw response;
            }
        });
};

const updateWorkData = (holderNumber, workData, file, history) => (dispatch, getState) => {
    const holderDocumentData = filterHolderDocumentData(getState().mortgageRequestDataProprietor.get('data').toJS(), holderNumber);

    const query = {
        nivelEstudios: workData.currentInfo.studyLevel || 'UNKNOWN',
        situacionLaboral: workData.currentInfo.jobSituation || 'UNKNOWN',
        tipoContratoTrabajo: workData.currentInfo.contractType || 'UNKNOWN',
        anioIncorporacionTrabajo: workData.currentInfo.jobIncorporationDate.year,
        mesIncorporacionTrabajo: workData.currentInfo.jobIncorporationDate.month,
        documentoDeIdentidad: {
            tipoDocumentoIdentidad: holderDocumentData.tipoDocumentoIdentidad,
            identificador: holderDocumentData.identificador
        },
        idCea: workData.ceaCode.idCea
    };


    if (workData.ceaCode.dateGenCea.year !== '') {
        const { dateGenCea } = workData.ceaCode
        query.fechaGenCea = `${dateGenCea.year}-${dateGenCea.month}-${dateGenCea.day}`;
    } else {
        query.fechaGenCea = '';
    }

    map(workData.ceaCode.inputsCode, (code, idx) => {
        query[`codigoCea${idx}`] = code;
    });

    if (file) {
        const typeFile = query.situacionLaboral === FUNCIONARIO_MUFACE ? NOM : VL;
        uploadWorkFile(query, holderDocumentData, file, typeFile, dispatch, getState, holderNumber, history);
    } else {
        uploadWorkData(query, dispatch, holderNumber, history);
    }
};

const requestWorkData = (holderDocumentData, dispatch) => {
    const requestData = {
        query: {
            tipoDocumentoIdentidad: holderDocumentData.tipoDocumentoIdentidad,
            numeroDocumento: holderDocumentData.identificador
        }
    };

    dispatch(requestHolderWorkInProgress());
    return APIPost({
        dispatch,
        endpoint: 'mortgageRequesWorkDataRequest',
        params: requestData
    })
        .then(response => dispatch(requestHolderWorkSuccess(response)))
        .catch(response => {
            if (!!response.error) {
                dispatch(requestDataProprietorFailure(response.error));
                dispatch(requestHolderWorkFailure(response.error));
            } else {
                throw response;
            }
        });
}

const filterHolderInfoForRequestJobData = (holderNumber, data, dispatch) => {
    const holderDocumentData = filterHolderDocumentData(data, holderNumber);

    if (holderDocumentData && !!holderDocumentData.identificador && holderDocumentData.identificador !== 'UNKNOWN') {
        requestWorkData(holderDocumentData, dispatch)
    } else {
        dispatch(requestDataProprietorFailure(formatText('common-error')));
    }
}

const getHolderData = holderNumber => (dispatch, getState) => {
    dispatch(requestDataProprietorInProgress());
    return APIPost({
        dispatch,
        endpoint: 'mortgageRequestDataProprietorRequest'
    })
        .then(responseDataProprietor => dispatch(requestDataWork(responseDataProprietor)))
        .then(response => {
            dispatch(requestDataProprietorSuccess(response));
            getHoldersPersonalAndEconomicData(dispatch, getState, true);
            filterHolderInfoForRequestJobData(holderNumber, response.data, dispatch);
        })
        .catch(response => {
            if (!!response.error) {
                dispatch(requestDataProprietorFailure(response.error));
            } else {
                throw response;
            }
        });
}

module.exports = {
    getHolderData,
    requestHolderWork,
    updateWorkData,
    requestWorkFlag,
    reset,
    cleanState
};
